import {
  PACK_SIZE_14_DAY,
  PACK_SIZE_28_DAY,
  PACK_SIZE_7_DAY,
  TOTAL_PACKS_14_DAY,
  TOTAL_PACKS_28_DAY,
  TOTAL_PACKS_7_DAY,
} from "@/helpers/lookupMaps";

export const SELLING_PLANS = {
  DAY_7: "690060558676",
  DAY_14: "690060525908",
  DAY_28: "690060591444",
} as const;

export type SellingPlanKey = keyof typeof SELLING_PLANS;

// Figma copy style: lowercase "day plan" (plan rows + review order card)
export const SELLING_PLAN_LABELS: Record<SellingPlanKey, string> = {
  DAY_7: "7 day plan",
  DAY_14: "14 day plan",
  DAY_28: "28 day plan",
};

export type PlanDays = 7 | 14 | 28;

/**
 * TEMPORARY LOCK: plan lengths listed here stay visible on the quiz plan step
 * but cannot be selected or purchased on NEW plans — the card shows
 * LOCKED_PLAN_LABEL instead. Existing subscribers and account-portal
 * re-orders are unaffected (the Recharge selling plans stay live).
 *
 * Currently empty — all plans purchasable. To lock a plan again, add its
 * days here (e.g. [28]). Nothing else needs touching.
 */
export const LOCKED_PLAN_DAYS: PlanDays[] = [];

export const LOCKED_PLAN_LABEL = "Available upon re-ordering";

export const isPlanLocked = (days: number): boolean =>
  LOCKED_PLAN_DAYS.includes(days as PlanDays);

/**
 * Always round UP to next 50g bucket so you never under-allocate.
 */
export function toDailyKey50(dailyGrams: number): number {
  if (!dailyGrams || dailyGrams <= 0) return 50;
  return Math.ceil(dailyGrams / 50) * 50;
}

/**
 * Chosen pack size from the table.
 */
export function getChosenPackSize(
  dailyGrams: number,
  planDays: PlanDays
): number {
  const key = toDailyKey50(dailyGrams);

  const size =
    planDays === 7
      ? PACK_SIZE_7_DAY[key]
      : planDays === 14
      ? PACK_SIZE_14_DAY[key]
      : PACK_SIZE_28_DAY[key];

  if (!size) {
    console.warn("No packSize found for:", { dailyGrams, key, planDays });
    return 800;
  }

  return size;
}

/**
 * Total packs from the table (THIS drives pricing).
 */
export function getChosenTotalPacks(
  dailyGrams: number,
  planDays: PlanDays
): number {
  const key = toDailyKey50(dailyGrams);

  const packs =
    planDays === 7
      ? TOTAL_PACKS_7_DAY[key]
      : planDays === 14
      ? TOTAL_PACKS_14_DAY[key]
      : TOTAL_PACKS_28_DAY[key];

  if (!packs) {
    console.warn("No totalPacks found for:", { dailyGrams, key, planDays });
    // fallback: safest approximation if key missing
    const packSize = getChosenPackSize(dailyGrams, planDays);
    const totalGrams = key * planDays;
    return Math.max(1, Math.ceil(totalGrams / packSize));
  }

  return packs;
}

/**
 * Convenience: everything you need to price a dog for a plan.
 * (dailyKey is the rounded 50g bucket we used)
 */
export function getPackPricingConfig(dailyGrams: number, planDays: PlanDays) {
  const dailyKey = toDailyKey50(dailyGrams);
  const packSize = getChosenPackSize(dailyKey, planDays);
  const totalPacks = getChosenTotalPacks(dailyKey, planDays);
  const totalGrams = dailyKey * planDays;

  return { dailyKey, packSize, totalPacks, totalGrams };
}

/**
 * Compute pouches needed. (Still useful elsewhere, but NOT the source of truth for plan pricing.)
 */
export function allocatePouches(totalGrams: number, packSize: number) {
  const pouches = Math.ceil(totalGrams / packSize);
  return {
    packSize,
    pouches,
    totalDelivered: pouches * packSize,
    overshoot: pouches * packSize - totalGrams,
  };
}

/** LEGACY – no longer used by checkout logic */
export function getSellingPlanId(days: PlanDays): string {
  if (days === 7) return SELLING_PLANS.DAY_7;
  if (days === 14) return SELLING_PLANS.DAY_14;
  return SELLING_PLANS.DAY_28;
}

export function buildMealAutoConfigSig({
    planDays,
    dailyKey,
    packSize,
    totalPacks,
}: {
    planDays: number;
    dailyKey: number;
    packSize: number;
    totalPacks: number;
}) {
    return `${planDays}|${dailyKey}|${packSize}|${totalPacks}`;
}